import { Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormlyFieldConfig, FormlyForm } from "@ngx-formly/core";
import { posthog } from "posthog-js";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FluidModule } from "primeng/fluid";
import { finalize } from "rxjs";
import { NewInvitationDialogConfig } from "./new-invitation-dialog.types";
import { ApiCreateInvitationsRequest } from "../../../../apiClient/models";
import { AccessRoleDetails } from "../../../../domain/access-role";
import { OrganizationDetails } from "../../../../domain/organization";
import { FormService } from "../../../../services/form.service";
import { InvitationsService } from "../../../../services/invitations.service";
import { ToastService } from "../../../../services/toast.service";
import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";
import { FormlyFormOptionsWithFormState } from "../../../../shared/types/form";

@Component({
  templateUrl: "./new-invitation.dialog.html",
  imports: [
    ...primeNgPageModules,
    ReactiveFormsModule,
    FormlyForm,
    DialogModule,
    ButtonModule,
    FluidModule,
    TranslocoDirective,
  ],
})
export class NewInvitationDialog {
  private orgData?: OrganizationDetails;

  public form = new FormGroup({});
  public model: ApiCreateInvitationsRequest = {} as ApiCreateInvitationsRequest;
  public fields: FormlyFieldConfig[];
  public options: FormlyFormOptionsWithFormState = {
    formState: {
      disabled: false,
    },
  };
  public loading = false;

  constructor(
    public readonly ref: DynamicDialogRef,
    private readonly formService: FormService,
    private readonly config: DynamicDialogConfig<NewInvitationDialogConfig>,
    private readonly translocoService: TranslocoService,
    private readonly invitationsService: InvitationsService,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef,
  ) {
    // TODO move the prepareFormFields parameter to a separate file to keep this file clean

    this.orgData = config.data?.organization;

    if (!this.orgData) {
      this.fields = [];
      return;
    }

    this.fields = this._buildFormFields(this.orgData.accessRoles);
  }

  private _buildFormFields(accessRoles: AccessRoleDetails[]): FormlyFieldConfig[] {
    const orgData = this.orgData!;

    return this.formService.prepareFormFields([
      {
        fieldGroupClassName: "flex flex-col gap-5",
        fieldGroup: [
          {
            key: "emails",
            type: "repeat",
            props: {
              addText: {
                en: "Add email",
                fi: "Lisää sähköposti",
                sv: "Lägg till e-post",
                no: "Legg til e-post",
              },
              label: {
                en: "Emails",
                fi: "Sähköpostit",
                sv: "E-postadresser",
                no: "E-poster",
              },
              required: true,
            },
            fieldArray: {
              type: "text",
              props: {
                placeholder: {
                  en: "name@example.com",
                  fi: "nimi@esimerkki.com",
                  sv: "namn@exempel.com",
                  no: "navn@eksempel.no",
                },
                required: true,
              },
              validators: {
                validation: ["email"],
              },
            },
            expressions: {
              "props.disabled": (config) => config.options?.formState.disabled,
            },
          },
          {
            key: "languageKind",
            type: "select",
            props: {
              label: {
                en: "Language",
                fi: "Kieli",
                sv: "Språk",
                no: "Språk",
              },
              required: true,
              options: [
                {
                  value: "English",
                  label: { en: "English", fi: "Englanti", sv: "Engelska", no: "Engelsk" },
                },
                {
                  value: "Finnish",
                  label: { en: "Finnish", fi: "Suomi", sv: "Finska", no: "Finsk" },
                },
                {
                  value: "Swedish",
                  label: { en: "Swedish", fi: "Ruotsi", sv: "Svenska", no: "Svensk" },
                },
                {
                  value: "Norwegian",
                  label: { en: "Norwegian", fi: "Norja", sv: "Norska", no: "Norsk" },
                },
              ],
            },
            expressions: {
              "props.disabled": (config) => config.options?.formState.disabled,
            },
          },
          // AccessRole dropdown
          {
            key: "userAccessRoleId",
            type: "select",
            props: {
              required: true,
              label: {
                en: "Access Role",
                fi: "Käyttöoikeusrooli",
                sv: "Åtkomstroll",
                no: "Tilgangsrolle",
              },
              options: accessRoles.map((role) => ({
                value: role.id,
                label: { en: role.name, fi: role.name, sv: role.name, no: role.name },
              })),
            },
            expressions: {
              "props.disabled": (config) => config.options?.formState.disabled,
            },
          },
          {
            key: "defaultApprovalGroupId",
            type: "select",
            props: {
              label: {
                en: "Process Approvers",
                fi: "Prosessien hyväksyjät",
                sv: "Processförhandlare",
                no: "Prosessgodkjennere",
              },
              description: {
                en: "This approval group will be automatically selected for all processes that require approval and are started by this user.",
                fi: "Tämä hyväksyntäryhmä asetetaan automaattisesti kaikkiin prosesseihin, jotka vaativat hyväksynnän ja jotka tämä käyttäjä aloittaa.",
                sv: "Denna godkännande grupp kommer att väljas automatiskt för alla processer som kräver godkännande och som den här användaren startar.",
                no: "Denne godkjenner-gruppen blir automatisk valgt for alle prosesser som krever godkjenning, som ble opprettet av denne brukeren",
              },

              options: orgData.approvalGroups.map((approvalGroup) => ({
                value: approvalGroup.id,
                label: {
                  en: approvalGroup.name,
                  fi: approvalGroup.name,
                  sv: approvalGroup.name,
                  no: approvalGroup.name,
                },
              })),
            },
            expressions: {
              "props.disabled": (config) => config.options?.formState.disabled,
            },
          },
        ],
      },
    ]);
  }

  public resetInvitationForm(): void {
    this.form.reset();
    this.model = {} as ApiCreateInvitationsRequest;
  }

  public submitInvitation(): void {
    if (this.form.valid) {
      if (!this.orgData) {
        this.toastService.error(this.translocoService.translate("app.organization-settings.inviteDialog.error"));
        return;
      }

      this.loading = true;
      this.options.formState.disabled = true;

      const request: ApiCreateInvitationsRequest = {
        emails: this.model.emails,
        languageKind: this.model.languageKind,
        organizationId: this.orgData.id,
        userAccessRoleId: this.model.userAccessRoleId,
        defaultApprovalGroupId: this.model.defaultApprovalGroupId ?? undefined,
      };

      this.invitationsService
        .createInvitations$(request)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loading = false;
            this.options.formState.disabled = false;
          }),
        )
        .subscribe({
          next: (response) => {
            this.toastService.success(
              this.translocoService.translate("app.organization-settings.inviteDialog.success"),
            );

            posthog.capture("invitation_sent");

            this.ref.close(response);
          },
          error: (error) => {
            if (error.status === 400 && error.error?.kind && error.error?.emails) {
              const translationKey = this.getFailureTranslationKey(error.error.kind);
              this.toastService.error(
                this.translocoService.translate(translationKey, { emails: error.error.emails.join(", ") }),
              );
            } else {
              this.toastService.error(this.translocoService.translate("app.organization-settings.inviteDialog.error"));
              console.error("Error creating invitation:", error);
            }
          },
        });
    }
  }

  // https://jsverse.gitbook.io/transloco/tools/keys-manager-tkm/keys-extractor#dynamic-keys
  /**
   * t(app.organization-settings.inviteDialog.failureReason.invitationAlreadyExists)
   * t(app.organization-settings.inviteDialog.failureReason.userAlreadyInOrganization)
   * t(app.organization-settings.inviteDialog.failureReason.invitationDuplicateEmail)
   * t(app.organization-settings.inviteDialog.failureReason.invitationInternalEmail)
   * t(app.organization-settings.inviteDialog.failureReason.unknown)
   */
  private getFailureTranslationKey(kind: string): string {
    const knownKinds = [
      "invitationAlreadyExists",
      "userAlreadyInOrganization",
      "invitationDuplicateEmail",
      "invitationInternalEmail",
    ];

    const validKind = knownKinds.includes(kind) ? kind : "unknown";
    return `app.organization-settings.inviteDialog.failureReason.${validKind}`;
  }
}
